// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOrder, TyOrderItem } from "@/types";
import OrderContacts from "@/UI/components/OrderContacts";
import { formatOrderTime, pickLangValue, readLangs } from "@/utils";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/** How many lines a card shows before it stops listing and starts counting. See the note below. */
const SHOWN_ITEMS = 2;

// --- Types --------------------------------------------------------------------------------------

type Props = {
    order: TyOrder;
    index: number;
    /** The one being worked on: it keeps the brand ring while the rest of the list steps back. */
    isActive?: boolean;
    isDimmed?: boolean;
    /**
     * It changed under us and no longer belongs in the list the filters asked for. It stays where it
     * is rather than vanishing — a card disappearing while someone is reading it is the confusing
     * part — but it is a receipt of what happened now, not a row to act on, so it stops responding.
     */
    isStale?: boolean;
    onOpen?: () => void;
    /** Absent for a client who may view orders but not act on them. */
    onMore?: (anchor: DOMRect) => void;
    busy?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One order at a glance: who it is for, where it goes, what it comes to, and how far along it is.
 *
 * **Every card is the same height, whatever is in the order.** A counter scans this list under
 * pressure, and a list whose rows jump from three lines to eleven because somebody ordered for a
 * table of six cannot be scanned at all. So the lines stop at two and the rest becomes a count, and
 * the actions live behind one button instead of a row that grows with the order's state — the card
 * is a summary, and the popup behind it is where an order is actually worked on.
 */
export default function OrderCard({ order, index, isActive, isDimmed, isStale, onOpen, onMore, busy }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const branchName = order.branch ? pickLangValue(readLangs(order.branch.name, ["ar", "en"]), lang) : t.orders.mainMenu;

    /* Each line is read in the language this dashboard is in, out of the snapshot the order kept. */
    const itemName = (item: TyOrderItem) => pickLangValue(readLangs(item.name, ["ar", "en"]), lang);
    const itemLabel = (item: TyOrderItem) => pickLangValue(readLangs(item.label, ["ar", "en"]), lang);
    const placedAt = formatOrderTime(order.createdAt, lang);

    /* One order is not a returning customer; two is. Counted across everything this phone ever
       ordered here, never across whatever the list happens to be filtered to. */
    const isReturning = (order.customer?.ordersCount ?? 0) > 1;

    const shown = order.items.slice(0, SHOWN_ITEMS);
    const rest = order.items.length - shown.length;

    const [showContacts, setShowContacts] = useState(false);

    // --- Handlers --------------------------------------------------------------------------------

    const handleMore = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onMore?.(event.currentTarget.getBoundingClientRect());
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <article
            className="order-card"
            data-active={!!isActive}
            data-dimmed={!!isDimmed}
            data-stale={!!isStale}
            title={isStale ? t.orders.changedHint : undefined}
            style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
            role={onOpen && !isStale ? "button" : undefined}
            tabIndex={onOpen && !isStale ? 0 : undefined}
            onClick={isStale ? undefined : onOpen}
            onKeyDown={(event) => {
                if (isStale || !onOpen || (event.key !== "Enter" && event.key !== " ")) return;
                event.preventDefault();
                onOpen();
            }}
        >
            {/* Nothing on a changed card may be used — not even the phone number, which is the one
                control that still looked clickable. A layer over the whole card is what makes that
                true by construction rather than by remembering to disable each control. */}
            {isStale && (
                <div className="order-card-veil">
                    <span className="order-card-veil-tag">{t.orders.changedBadge}</span>
                </div>
            )}

            {/* The corner the menu's discount ribbon uses — a fact about the order, out of the way
                of everything the counter has to read. */}
            {order.edits && order.edits.length > 0 && (
                <div className="order-card-ribbon" aria-label={t.orders.editedBadge}>
                    <span className="order-card-ribbon-text">{t.orders.editedBadge}</span>
                </div>
            )}

            <header className="order-card-header">
                <div className="order-card-id">
                    <span className="order-card-number">#{order.orderNumber}</span>
                    <span className="order-card-status" data-status={order.status}>
                        {t.orders.status[order.status]}
                    </span>
                </div>
                <span className="order-card-time">{placedAt}</span>
            </header>

            <div className="order-card-meta">
                {/* A table order says which table in the chip after this one, which already says it
                    is at a table — so the kind is only named when it is not. */}
                {order.type !== "table" && <span className="order-card-chip">{t.orders.type[order.type] ?? order.type}</span>}
                {order.type === "table" && order.tableNumber && (
                    <span className="order-card-chip">
                        {t.orders.tableLabel} {order.tableNumber}
                    </span>
                )}
            </div>

            {/* The branch belongs to the items, not to the row of chips: it is what these prices and
                this availability came from, and that is a sentence about the list under it. */}
            <div className="order-card-items-box">
                <p className="order-card-source">{t.orders.itemsFrom.replace("{menu}", branchName)}</p>

                <ul className="order-card-items">
                    {shown.map((item, i) => (
                        <li key={`${item.product}-${item.priceId}-${i}`} className="order-card-item">
                            <span className="order-card-item-qty">×{item.quantity}</span>
                            <span className="order-card-item-name">
                                {itemName(item)}
                                {itemLabel(item) && <span className="order-card-item-label"> — {itemLabel(item)}</span>}
                            </span>
                            <span className="order-card-item-price">{item.price * item.quantity}</span>
                        </li>
                    ))}
                    {rest > 0 && <li className="order-card-item order-card-item--rest">{t.orders.moreItems.replace("{count}", String(rest))}</li>}
                </ul>
            </div>

            <footer className="order-card-footer">
                <div className="order-card-total">
                    <span className="order-card-total-label">{t.orders.totalLabel}</span>
                    <span className="order-card-total-value">{order.totalPrice}</span>
                </div>

                <div className="order-card-actions" onClick={(event) => event.stopPropagation()}>
                    <OrderContacts phone={order.customerPhone} isOpen={showContacts} onToggle={() => setShowContacts((prev) => !prev)}>
                        {/* A fact about the person, so it rides with their number rather than with
                            the chips that say where the order is going. */}
                        {isReturning && <span className="order-card-regular">{t.orders.returningBadge}</span>}
                    </OrderContacts>

                    {onMore && !isStale && (
                        <button
                            type="button"
                            className="order-card-more"
                            onClick={handleMore}
                            disabled={busy}
                            aria-label={t.orders.moreActions}
                            title={t.orders.moreActions}
                        >
                            <img src="/icons/more-icon.svg" alt="" width={16} height={16} />
                        </button>
                    )}
                </div>
            </footer>
        </article>
    );
}
